import React, { useState } from "react";
import {
  Layout,
  Card,
  Space,
  Row,
  Col,
  DatePicker,
  Select,
  Button,
  Table,
  Statistic,
  Typography,
  message,
} from "antd";
import {
  FilterOutlined,
  CarOutlined,
  DollarOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getTicketParkingStatistics } from "../../../api/statistics/statisticsRequest";
import { formatAmountInFrench } from "../../../utils/constants";

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title } = Typography;

const TicketParkingStatistics = () => {
  const [dateRange, setDateRange] = useState(null);
  const [selectedGroupBy, setSelectedGroupBy] = useState(null);

  const [ticketParkingData, setTicketParkingData] = useState(null);
  const [loading, setLoading] = useState(false);

  const filterTicketParkingStatistics = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning("Veuillez sélectionner une période");
      return;
    }

    setLoading(true);

    try {
      const data = await getTicketParkingStatistics({
        start_date: dateRange[0].format("YYYY-MM-DD HH:mm:ss"),
        end_date: dateRange[1].format("YYYY-MM-DD HH:mm:ss"),
        group_by: selectedGroupBy,
      });
      setTicketParkingData(data);
    } catch (error) {
      setTicketParkingData(null);
      message.error("Échec de la récupération des données");
    } finally {
      setLoading(false);
    }
  };

  const timelineChartOptions = {
    chart: {
      type: "column",
      zoomType: "x",
    },
    title: {
      text: "Chronologie des Tickets de Stationnement",
    },
    xAxis: {
      categories: ticketParkingData
        ? ticketParkingData.timeline.map((item) => item.date)
        : [],
      crosshair: true,
    },
    yAxis: {
      min: 0,
      title: {
        text: "Nombre de Tickets",
      },
    },
    credits: {
      enabled: false,
    },
    series: [
      {
        name: "Tickets de Stationnement",
        data: ticketParkingData
          ? ticketParkingData.timeline.map((item) => item.count)
          : [],
        color: "#52c41a",
      },
    ],
  };

  const zoneChartOptions = {
    chart: {
      type: "column",
    },
    title: {
      text: "Statistiques par Contrat",
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: ticketParkingData?.by_zone.map((zone) => zone.zone_name),
      crosshair: true,
    },
    yAxis: [
      {
        min: 0,
        title: {
          text: "Nombre de Tickets",
        },
      },
      {
        min: 0,
        title: {
          text: "Montant Total",
        },
        opposite: true,
      },
    ],
    tooltip: {
      shared: true,
    },
    legend: {
      align: "left",
      verticalAlign: "top",
      backgroundColor: "rgba(255,255,255,0.8)",
      borderColor: "#CCC",
      borderWidth: 1,
      shadow: false,
    },
    series: [
      {
        name: "Nombre de Tickets",
        data: ticketParkingData?.by_zone.map((zone) => zone.total_tickets),
        yAxis: 0,
        color: "#52c41a",
      },
      {
        name: "Montant Total",
        data: ticketParkingData?.by_zone.map((zone) =>
          parseFloat(zone.total_amount)
        ),
        yAxis: 1,
        color: "#1890ff",
        tooltip: {
          valueDecimals: 2,
          valuePrefix: "",
          valueSuffix: " MAD",
        },
      },
    ],
  };

  // Calculate total revenue
  const totalRevenue = ticketParkingData?.by_zone.reduce(
    (sum, zone) => sum + parseFloat(zone.total_amount || 0),
    0
  );

  // Calculate average ticket price
  const averageTicketPrice =
    totalRevenue && ticketParkingData?.summary.total
      ? totalRevenue / ticketParkingData.summary.total
      : 0;

  return (
    <Layout className="min-h-screen">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card className="mb-6 shadow-sm">
          <Space direction="vertical" size="middle" className="w-full">
            <Row gutter={16} align="middle">
              <Col xs={24} sm={12} md={8}>
                <RangePicker
                  onChange={(dates) => setDateRange(dates)}
                  className="w-full"
                  placeholder={["Date début", "Date fin"]}
                  showTime={{ format: "HH:mm" }}
                />
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder="Groupé par"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedGroupBy(value)}
                  allowClear
                >
                  <Option value={"daily"}>Quotidien</Option>
                  <Option value={"weekly"}>Hebdomadaire</Option>
                  <Option value={"monthly"}>Mensuel</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Button
                  type="primary"
                  icon={<FilterOutlined />}
                  onClick={filterTicketParkingStatistics}
                  loading={loading}
                  className="w-full"
                >
                  Rechercher
                </Button>
              </Col>
            </Row>
          </Space>
        </Card>

        {ticketParkingData && (
          <Space direction="vertical" size="large" className="w-full">
            <Card className="shadow-sm">
              <Title level={2} className="text-center mb-6">
                Résumé des Tickets de Stationnement
              </Title>
              <Row gutter={16} className="mb-6">
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Total Tickets"
                    value={ticketParkingData.summary.total}
                    prefix={<FileTextOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Revenus Total"
                    value={formatAmountInFrench(
                      totalRevenue?.toFixed(2) || "0"
                    )}
                    prefix={<DollarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Prix Moyen"
                    value={formatAmountInFrench(
                      averageTicketPrice?.toFixed(2) || "0"
                    )}
                    prefix={<CarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Zones Actives"
                    value={ticketParkingData.by_zone.length}
                    prefix={<CarOutlined />}
                  />
                </Col>
              </Row>
            </Card>

            <Row gutter={16}>
              <Col xs={24}>
                <Card className="shadow-sm">
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={timelineChartOptions}
                  />
                </Card>
              </Col>
            </Row>

            <Card className="shadow-sm mt-6">
              <Title level={3} className="mb-4">
                Statistiques par Contrat
              </Title>
              <HighchartsReact
                highcharts={Highcharts}
                options={zoneChartOptions}
              />
            </Card>

            <Card className="shadow-sm mt-6">
              <Title level={3} className="mb-4">
                Détails des Statistiques par Contrat
              </Title>
              <Table
                dataSource={ticketParkingData?.by_zone}
                columns={[
                  {
                    title: "Contrat",
                    dataIndex: "zone_name",
                    key: "zone_name",
                  },
                  {
                    title: "Nombre de Tickets",
                    dataIndex: "total_tickets",
                    key: "total_tickets",
                    align: "center",
                    sorter: (a, b) => a.total_tickets - b.total_tickets,
                  },
                  {
                    title: "Montant Moyen",
                    dataIndex: "average_amount",
                    key: "average_amount",
                    align: "right",
                    sorter: (a, b) =>
                      parseFloat(a.average_amount) -
                      parseFloat(b.average_amount),
                    render: (text) =>
                      formatAmountInFrench(parseFloat(text).toFixed(2)),
                  },
                  {
                    title: "Montant Total",
                    dataIndex: "total_amount",
                    key: "total_amount",
                    align: "right",
                    sorter: (a, b) =>
                      parseFloat(a.total_amount) - parseFloat(b.total_amount),
                    render: (text) =>
                      formatAmountInFrench(parseFloat(text).toFixed(2)),
                  },
                ]}
                pagination={false}
                rowKey="zone_id"
              />
            </Card>
          </Space>
        )}
      </Content>
    </Layout>
  );
};

export default TicketParkingStatistics;
